// @ts-nocheck
import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import { WishListModel } from "@insite/client-framework/Types/ApiModels";
import { WishListModelWithHasProduct } from "@store/Library/Widgets/BulkLists/useBulkLists";
import { Draft } from "immer";

export type BulkListState = Toolkit.Store.Bunzl.Pages.BulkList.State;
export const initialBulkListState: BulkListState = {
    bulkLists: undefined,
    filteredLists: undefined,
    isLoading: false,
    selectedProductId: undefined,
    wishListIdsWithProduct: undefined,
    filterBy: "all",
    innerSearchText: "",
    selectedListIds: undefined,
    showSelected: false,
};

const addHasProductToLists = (lists: WishListModel[], wishListIds: string[]): WishListModelWithHasProduct[] => {
    return lists?.map(list => {
        const hasProduct = wishListIds.includes(list.id);
        return { ...list, hasProduct };
    });
};
const filterListByShowSelected = (
    lists: WishListModelWithHasProduct[],
    showSelected: boolean,
    selectedListIds: string[],
) => {
    if (!showSelected) {
        return lists;
    }
    return lists.filter(list => selectedListIds.includes(list.id));
};
const filterLists = (state: BulkListState) => {
    const { bulkLists, selectedListIds, filterBy, innerSearchText, showSelected } = state;
    const filteredListsByProduct = filterListsByProduct(bulkLists || [], filterBy);
    const filteredListBySearchAndProduct = filterListsBySearch(filteredListsByProduct, innerSearchText);
    return filterListByShowSelected(filteredListBySearchAndProduct, showSelected, selectedListIds || []);
};

const filterListsByProduct = (
    lists: WishListModelWithHasProduct[],
    filterBy: "all" | "hasProduct" | "doesNotHaveProduct",
) => {
    if (filterBy === "all") {
        return lists;
    }
    return lists.filter(list => (filterBy === "hasProduct" ? list?.hasProduct : !list?.hasProduct));
};

const filterListsBySearch = (lists: WishListModelWithHasProduct[], innerSearchText: string) => {
    if (!innerSearchText) {
        return lists;
    }
    return lists.filter(
        list =>
            list.name.toLowerCase().includes(innerSearchText.toLowerCase()) ||
            list.description?.toLowerCase().includes(innerSearchText.toLowerCase()),
    );
};
export const bulkListActions = {
    "Store/Pages/bulkLists/setShowSelected": (draft: Draft<BulkListState>, action: { showSelected: boolean }) => {
        draft.showSelected = action.showSelected;
        draft.filteredLists = filterLists(draft);
    },
    "Store/Pages/bulkLists/setInnerSearchText": (draft: Draft<BulkListState>, action: { innerSearchText: string }) => {
        draft.innerSearchText = action.innerSearchText;
        draft.filteredLists = filterLists(draft);
    },
    "Store/Pages/bulkLists/setIsLoading": (draft: Draft<BulkListState>, action: { isLoading: boolean }) => {
        draft.isLoading = action.isLoading;
    },
    "Store/Pages/bulkLists/loadBulkList": (draft: Draft<BulkListState>, action: { bulkLists?: WishListModel[] }) => {
        draft.bulkLists = action.bulkLists;
        draft.filteredLists = filterLists(draft);
        if (draft.selectedProductId && draft.bulkLists) {
            draft.bulkLists = addHasProductToLists(draft.bulkLists, draft.wishListIdsWithProduct || []);
        }
    },
    "Store/Pages/bulkLists/setSelectedListIds": (
        draft: Draft<BulkListState>,
        action: { selectedListIds: string[] | undefined },
    ) => {
        draft.selectedListIds = action.selectedListIds;
        if (draft.showSelected && action.selectedListIds?.length) {
            draft.filteredLists = filterLists(draft);
        }
        if (draft.showSelected && !action.selectedListIds?.length) {
            draft.showSelected = false;
            draft.filteredLists = filterLists(draft);
        }
    },
    "Store/Pages/bulkLists/setSelectedProductId": (
        draft: Draft<BulkListState>,
        action: { selectedProductId: string | undefined },
    ) => {
        draft.selectedProductId = action.selectedProductId;
        if (!action.selectedProductId) {
            draft.wishListIdsWithProduct = undefined;
            const resetLists = draft.bulkLists?.map(list => ({ ...list, hasProduct: undefined }));
            draft = {
                ...initialBulkListState,
                bulkLists: resetLists,
                filteredLists: resetLists,
                selectedListIds: draft.selectedListIds,
            };
        }
    },
    "Store/Pages/bulkLists/setWishListIdsWithProduct": (
        draft: Draft<BulkListState>,
        action: { wishListIdsWithProduct: string[] | undefined },
    ) => {
        draft.wishListIdsWithProduct = action.wishListIdsWithProduct;
        draft.bulkLists = addHasProductToLists(draft.bulkLists as WishListModel[], action.wishListIdsWithProduct || []);
        draft.filteredLists = filterLists(draft);
    },
    "Store/Pages/bulkLists/setFilterBy": (
        draft: Draft<BulkListState>,
        action: { filterBy: "all" | "hasProduct" | "doesNotHaveProduct" },
    ) => {
        draft.filterBy = action.filterBy;
        draft.filteredLists = filterLists(draft);
    },
};

const bulkListReducer = createTypedReducerWithImmer(initialBulkListState, bulkListActions);
export type BulkListActions = Parameters<typeof bulkListReducer>[1];
export default bulkListReducer;
